import React from 'react';
import { ForeignActivitiesDirectItemModel } from 'forms/sections/foreign/activities/direct/types';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import ValidationElement from 'components/Form/ValidationElement';
import { CheckboxModel } from 'forms/shared/types';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Currency from 'components/Form/Currency';
import NotApplicable from 'components/Form/NotApplicable';
import Show from 'components/Form/Show';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import Text from 'components/Form/Text';
import { getSafeDateTime } from 'helpers/date';
import ErrorMessage from 'components/ErrorMessage';
import CoOwners from '../Shared/CoOwners';

interface Props extends Partial<ForeignActivitiesDirectItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks

  dispatch: Dispatch
  prefix: string
}

export default class DirectInterest extends ValidationElement<Props> {
  update = (queue: Partial<ForeignActivitiesDirectItemModel>) => {
    this.props.onUpdate({
      InterestTypes: this.props.InterestTypes,
      InterestType: this.props.InterestType,
      Acquired: this.props.Acquired,
      HowAcquired: this.props.HowAcquired,
      Cost: this.props.Cost,
      CostEstimated: this.props.CostEstimated,
      Value: this.props.Value,
      ValueEstimated: this.props.ValueEstimated,
      Relinquished: this.props.Relinquished,
      RelinquishedNotApplicable: this.props.RelinquishedNotApplicable,
      Explanation: this.props.Explanation,
      CoOwners: this.props.CoOwners,
      ...queue,
    });
  };

  updateInterestTypes = (values: CheckboxModel) => {
    const interestType = values.value;
    const selected = [...((this.props.InterestTypes || {}).values || [])];
    if (selected.includes(interestType)) {
      selected.splice(selected.indexOf(interestType), 1);
    } else {
      selected.push(interestType);
    }

    this.update({
      InterestTypes: { values: selected },
    });
  };

  updateInterestType = (values: ForeignActivitiesDirectItemModel['InterestType']) => {
    this.update({
      InterestType: values,
    });
  };

  updateAcquired = (values: ForeignActivitiesDirectItemModel['Acquired']) => {
    this.update({
      Acquired: values,
    });
  };

  updateHowAcquired = (values: ForeignActivitiesDirectItemModel['HowAcquired']) => {
    this.update({
      HowAcquired: values,
    });
  };

  updateCost = (values: ForeignActivitiesDirectItemModel['Cost']) => {
    this.update({
      Cost: values,
    });
  };

  updateCostEstimated = (values: ForeignActivitiesDirectItemModel['CostEstimated']) => {
    this.update({
      CostEstimated: values,
    });
  };

  updateValue = (values: ForeignActivitiesDirectItemModel['Value']) => {
    this.update({
      Value: values,
    });
  };

  updateValueEstimated = (values: ForeignActivitiesDirectItemModel['ValueEstimated']) => {
    this.update({
      ValueEstimated: values,
    });
  };

  updateRelinquished = (values: ForeignActivitiesDirectItemModel['Relinquished']) => {
    this.update({
      Relinquished: values,
    });
  };

  updateRelinquishedNotApplicable = (
    values: ForeignActivitiesDirectItemModel['RelinquishedNotApplicable']
  ) => {
    this.update({
      RelinquishedNotApplicable: values,
      Relinquished: values.applicable ? this.props.Relinquished : {},
      Explanation: values.applicable ? this.props.Explanation : {},
    });
  };

  updateExplanation = (values: ForeignActivitiesDirectItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateCoOwners = (values: ForeignActivitiesDirectItemModel['CoOwners']) => {
    this.update({
      CoOwners: values,
    });
  };

  render() {
    const { prefix, Relinquished, Explanation } = this.props;
    const attribute = `List.items[${this.props.$index}].Item`;
    const applicantBirthdateComparable = getSafeDateTime(this.props.applicantBirthdate);
    const acquiredDateComparable = getSafeDateTime(this.props.Acquired);
    const includeNAConfirmation = Boolean(Relinquished?.day || Relinquished?.month || Relinquished?.year || Explanation?.value);
    return (
      <div className="interest">
        <Field
          title={i18n.t('foreign.activities.direct.interest.heading.interestTypes')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InterestTypes`}
        >
          <p>{i18n.t('foreign.activities.direct.interest.para.checkAll')}</p>
          <CheckboxGroup
            className="interest-types option-list option-list-vertical"
            onError={this.props.onError}
            required={this.props.required}
            selectedValues={(this.props.InterestTypes || {}).values}
            attribute={`${attribute}.InterestTypes`}
          >
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.direct.interest.label.interestTypes.yourself')}
              value="Yourself"
              className="yourself"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.direct.interest.label.interestTypes.spouse')}
              value="Spouse"
              className="spouse"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t('foreign.activities.direct.interest.label.interestTypes.cohabitant')}
              value="Cohabitant"
              className="cohabitant"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
            <Checkbox
              name="interest-type"
              label={i18n.t(
                'foreign.activities.direct.interest.label.interestTypes.dependentChildren'
              )}
              value="DependentChildren"
              className="dependent-children"
              onError={this.props.onError}
              onUpdate={this.updateInterestTypes}
            />
          </CheckboxGroup>
        </Field>

        <Field
          title={i18n.t('foreign.activities.direct.interest.heading.interestType')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.InterestType`}
        >
          <Text
            name="InterestType"
            className="interest-type"
            {...this.props.InterestType}
            onUpdate={this.updateInterestType}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.InterestType`}
          />
        </Field>

        {Array.isArray(this.props.InterestTypes?.values) && this.props.InterestTypes?.values?.includes('Yourself')
          && applicantBirthdateComparable.valid && acquiredDateComparable.valid
          && applicantBirthdateComparable.datetime > acquiredDateComparable.datetime && (
            <ErrorMessage
              isWarning
              title="Oops, there may be a problem."
              message={[
                'This is before your birthdate. Is this your intention?',
                'Please check your answer.'
              ]}
            />
          )}

        <Field
          title={i18n.t('foreign.activities.direct.interest.heading.acquired')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Acquired`}
        >
          <DateControl
            name="Acquired"
            className="acquired"
            minDateEqualTo
            {...this.props.Acquired}
            onUpdate={this.updateAcquired}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Acquired`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.direct.interest.heading.howAcquired')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.HowAcquired`}
        >
          {i18n.t('foreign.activities.direct.interest.para.howAcquired')}
          <Textarea
            name="HowAcquired"
            className="how-acquired"
            {...this.props.HowAcquired}
            onUpdate={this.updateHowAcquired}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.HowAcquired`}
          />
        </Field>

        <Field
          title={i18n.t('foreign.activities.direct.interest.heading.cost')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Cost`}
        >
          <Currency
            name="Cost"
            className="cost"
            {...this.props.Cost}
            onUpdate={this.updateCost}
            min="0"
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Cost`}
          />
          <div className="flags">
            <Checkbox
              name="CostEstimated"
              label={i18n.t('foreign.activities.direct.interest.label.costEstimated')}
              toggle="false"
              {...this.props.CostEstimated}
              onUpdate={this.updateCostEstimated}
              onError={this.props.onError}
              attribute={`${attribute}.CostEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('foreign.activities.direct.interest.heading.value')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Value`}
        >
          <Currency
            name="Value"
            className="value"
            {...this.props.Value}
            onUpdate={this.updateValue}
            min="0"
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.Value`}
          />
          <div className="flags">
            <Checkbox
              name="ValueEstimated"
              label={i18n.t('foreign.activities.direct.interest.label.valueEstimated')}
              toggle="false"
              {...this.props.ValueEstimated}
              onUpdate={this.updateValueEstimated}
              onError={this.props.onError}
              attribute={`${attribute}.ValueEstimated`}
            />
          </div>
        </Field>

        <Field
          title={i18n.t('foreign.activities.direct.interest.heading.relinquished')}
          help="foreign.activities.direct.interest.help.relinquished"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Relinquished`}
        >
          <NotApplicable
            name="RelinquishedNotApplicable"
            {...this.props.RelinquishedNotApplicable}
            label={i18n.t('foreign.activities.direct.interest.label.relinquishedNotApplicable')}
            or={i18n.m('foreign.activities.direct.interest.label.or')}
            onError={this.props.onError}
            required={this.props.required}
            onUpdate={this.updateRelinquishedNotApplicable}
            attribute={`${attribute}.RelinquishedNotApplicable`}
            includeNAConfirmation={includeNAConfirmation}
          >
            <DateControl
              name="Relinquished"
              minDate={this.props.Acquired}
              minDateEqualTo
              className="relinquished"
              {...this.props.Relinquished}
              onUpdate={this.updateRelinquished}
              prefix="directControl"
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Relinquished`}
            />
          </NotApplicable>
        </Field>

        <Show when={(this.props.RelinquishedNotApplicable || {}).applicable}>
          <Field
            title={i18n.t('foreign.activities.direct.interest.heading.explanation')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Explanation`}
          >
            <Textarea
              name="Explanation"
              className="explanation"
              {...this.props.Explanation}
              onUpdate={this.updateExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Explanation`}
            />
          </Field>
        </Show>

        <CoOwners
          prefix={prefix}
          {...this.props.CoOwners}
          addressBooks={this.props.addressBooks}
          dispatch={this.props.dispatch}
          onUpdate={this.updateCoOwners}
          onError={this.props.onError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CoOwners`}
          peopleWhoKnowYou={this.props.peopleWhoKnowYou}
        />
      </div>
    );
  }
}

DirectInterest.defaultProps = {
  prefix: 'activities.direct.interest',
  addressBooks: {},
  RelinquishedNotApplicable: { applicable: true },
  applicantBirthdate: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
};
